import { useCallback, useState } from "react";
import { useLatestRef } from "@/hooks/useLatestRef";

/**
 * A multi-step sheet's page, scoped to ONE opening (`useSheetOpening`'s `openingKey`).
 *
 * Every opening starts on `first`, derived during render, so the first frame of a new opening is already right; a close
 * leaves the page alone, so the drawer animates out on what it showed. A page change is always made FOR an opening, and
 * one made for an opening that is no longer the one up is dropped: a removal, a save or a note that finishes after the
 * user closed that sheet and opened another — for another person, or the same one again — must not turn the newer
 * opening's page (a cover prompt for the old removal landing in it, a save bouncing it back to the actions).
 *
 * `setStepFor` answers whether the page turned, so a caller records only a step that actually opened.
 */
export const useOpeningStep = <S>(openingKey: number | null, first: S): { step: S; setStepFor: (forOpening: number | null, next: S) => boolean } => {
	const [state, setState] = useState<{ openingKey: number | null; step: S }>({ openingKey, step: first });
	const shownOpeningRef = useLatestRef(openingKey);
	const setStepFor = useCallback(
		(forOpening: number | null, next: S): boolean => {
			if (forOpening !== shownOpeningRef.current) return false;
			setState({ openingKey: forOpening, step: next });
			return true;
		},
		[shownOpeningRef],
	);
	return { step: state.openingKey === openingKey ? state.step : first, setStepFor };
};
